import {
  ModalBlocker,
  ModalHeader,
  ModalWindow,
  ScrollBars,
  useForm,
  Text,
  SuccessNotification,
  HintNotification,
} from '@epam/promo';
import './styles.scss';
import { joinUsersLinksWithMissingDefaultLinksFromTheForm } from './helpers/utils';
import { INotification, useUuiContext } from '@epam/uui';
import { NOTIFICATION_MESSAGES } from 'constants/uiConstants';
import { useEffect, useState } from 'react';
import { queryClient } from 'App';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useBlocker } from 'hooks/useBlocker';
import { useCloseBlocker } from 'hooks/useCloseBlocker';
import { useLeaveConfirm } from 'hooks/useLeaveConfirm';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import { AddToShowroomProps, LinkDictionary } from './models/interfaces';
import { STEPS_ADD_TO_SHOWROOM } from './constants';
import { LINK_TYPES } from '../project-links/constants';
import { getLinkByType } from './helpers/utils';
import { PROJECT_LINKS } from './helpers/utils';
import { MAX_PROJECT_LINKS } from 'constants/uiConstants';
import { addToShowroomSchema } from './models/validationSchema';
import { prepareFormData } from 'modules/project-management/update/utils';
import {
  IProjectLink,
  ProjectLinkType,
} from 'modules/project-management/models/interfaces';
import { showErrorMessages } from 'component/errorMessagesRenderer/errorMessagesRenderer';
import { validationErrors } from 'models/errors';
import Footer from './Footer';
import StepperController from 'component/Stepper';
import WarningMessage from '../../../../../component/Artifacts/WarningMessage';
import OptionalArtifactsForm from './tabs/OptionalArtifactsForm';
import RequiredArtifactsForm from './tabs/RequiredArtifactsForm';
import ProjectManagementService from 'services/api/project-management/projectManagementService';
import AboutProject from './tabs/AboutProjectTab';
import { useUserInfo } from 'services/auth-client/user-info-provider';
import {
  isUserWithEditorRole,
  isUserWithEventOrganizerRole,
} from 'utils/rolesUtils';

function AddToShowroom({
  modalProps,
  info,
  links,
  userHasActiveRepository,
  enableCancelShowroomButton,
  industries,
}: AddToShowroomProps) {
  const [currentLinkCount, setCurrentLinkCount] = useState(0);
  const [dictionary, setDictionary] = useState<ProjectLinkType[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});

  const svc = useUuiContext();
  const { userInfo } = useUserInfo();
  const isEditor: boolean = isUserWithEditorRole(userInfo);
  const isEventOrganizer: boolean = isUserWithEventOrganizerRole(userInfo);
  const editorSwitch = localStorage.getItem('editorSwitch');
  const steps = [
    STEPS_ADD_TO_SHOWROOM.ABOUT_PROJECT,
    STEPS_ADD_TO_SHOWROOM.REQUIRED_ARTIFACTS,
    STEPS_ADD_TO_SHOWROOM.OPTIONAL_ARTIFACTS,
  ];
  const isReachedLinkLimit = currentLinkCount === MAX_PROJECT_LINKS;
  const completeLinks = joinUsersLinksWithMissingDefaultLinksFromTheForm(links);
  const { DOCS_DECKS, SLIDE_DECK, CASE_STUDY, EMAIL, INDUSTRIES } = LINK_TYPES;
  const gitlabUrl = info?.labUrl;

  // Get initial industry type from industries prop
  function getInitialIndustryType(
    industriesData?: any[]
  ): 'any' | 'specific' | undefined {
    if (!industriesData || industriesData.length === 0) {
      return undefined;
    }
    if (industriesData.length === 1 && industriesData[0].externalId === '-1') {
      return 'any';
    }
    return 'specific';
  }

  const { lens, save, isChanged, validate } = useForm({
    validationOn: 'save',
    value: {
      GIT_REPO: gitlabUrl,
      projectLinks: completeLinks,
      userHasActiveRepository: userHasActiveRepository,
      industries: industries ?? [],
      industryType: getInitialIndustryType(industries),
      isEditor,
      isEventOrganizer,
      editorSwitch,
    },
    onSave: async (showroomFormData) => {
      useBlocker(svc);
      const filterredLinks = showroomFormData.projectLinks
        .filter(
          (link: IProjectLink) => link.link && typeof link.link === 'string'
        )
        .map((link: IProjectLink) => ({
          ...link,
          link: link.linkType === EMAIL ? `mailto:${link.link}` : link.link,
        }));
      const slideDeck = getLinkByType(
        showroomFormData.projectLinks,
        SLIDE_DECK
      );
      const caseStudy = getLinkByType(
        showroomFormData.projectLinks,
        CASE_STUDY
      );
      const addToShowroomData: any = {
        id: parseInt(info.id),
        slideDeck: slideDeck?.file,
        caseStudy: caseStudy?.file,
        projectLinks: filterredLinks,
        industries: showroomFormData.industries,
        ess:
          localStorage.getItem('editorSwitch') === null
            ? false
            : localStorage.getItem('editorSwitch'),
      };
      const preparedFormData = prepareFormData(addToShowroomData);
      await ProjectManagementService.addShowroomRequest(
        parseInt(info.id),
        preparedFormData
      );
      await Promise.all([
        queryClient.refetchQueries([QUERY_KEYS.PROJECTS.PROJECT_DETAILS]),
      ]);
      return Promise.resolve({ form: showroomFormData });
    },
    onSuccess: (showroomFormData) => {
      useCloseBlocker(svc);
      modalProps.success(true);
      enableCancelShowroomButton();
      const slideDeck = getLinkByType(
        showroomFormData.projectLinks,
        SLIDE_DECK
      );
      const knowlegeBase = getLinkByType(
        showroomFormData.projectLinks,
        DOCS_DECKS
      );
      const gitUrl = showroomFormData.GIT_REPO;
      svc.uuiNotifications
        .show(
          (props: INotification) => (
            <SuccessNotification {...props}>
              <Text>
                {NOTIFICATION_MESSAGES.success.createdShowroomRequest}
              </Text>
            </SuccessNotification>
          ),
          { position: 'bot-left', duration: 5 }
        )
        .catch(() => null);
      {
        showroomFormData.industries.length === 0 &&
          svc.uuiNotifications
            .show((props: INotification) => (
              <HintNotification {...props}>
                <Text>
                  {NOTIFICATION_MESSAGES.success.noIndustriesSelected}
                </Text>
              </HintNotification>
            ))
            .catch(() => null),
          (slideDeck?.file == undefined ||
            knowlegeBase?.link == undefined ||
            gitUrl == undefined) &&
            svc.uuiNotifications
              .show((props: INotification) => (
                <HintNotification {...props}>
                  {slideDeck?.file === undefined &&
                    knowlegeBase?.link !== undefined &&
                    gitUrl !== undefined && (
                      <Text>{NOTIFICATION_MESSAGES.success.noSlideDeck}</Text>
                    )}
                  {slideDeck?.file !== undefined &&
                    knowlegeBase?.link === undefined &&
                    gitUrl !== undefined && (
                      <Text>
                        {NOTIFICATION_MESSAGES.success.noKnowledegeBaseActivity}
                      </Text>
                    )}
                  {slideDeck?.file === undefined &&
                    knowlegeBase?.link === undefined &&
                    gitUrl !== undefined && (
                      <Text>
                        {
                          NOTIFICATION_MESSAGES.success
                            .noKnowledegeBaseActivityAndNoSlideDeck
                        }
                      </Text>
                    )}
                  {slideDeck?.file === undefined &&
                    knowlegeBase?.link !== undefined &&
                    gitUrl !== undefined && (
                      <Text>{NOTIFICATION_MESSAGES.success.noSlideDeck}</Text>
                    )}
                  {slideDeck?.file === undefined &&
                    knowlegeBase?.link !== undefined &&
                    gitUrl === undefined && (
                      <Text>
                        {
                          NOTIFICATION_MESSAGES.success
                            .noGitlabActivityAndNoSlideDeck
                        }
                      </Text>
                    )}
                  {slideDeck?.file !== undefined &&
                    knowlegeBase?.link === undefined &&
                    gitUrl === undefined && (
                      <Text>
                        {
                          NOTIFICATION_MESSAGES.success
                            .noGitlabActivityAndNoKnowledegeBaseActivity
                        }
                      </Text>
                    )}
                  {slideDeck?.file === undefined &&
                    knowlegeBase?.link === undefined &&
                    gitUrl === undefined && (
                      <Text>
                        {
                          NOTIFICATION_MESSAGES.success
                            .noGitlabActivityAndNoKnowledegeBaseActivityAndNoSlideDeck
                        }
                      </Text>
                    )}
                </HintNotification>
              ))
              .catch(() => null);
      }
    },
    onError: (errors: validationErrors) => {
      useCloseBlocker(svc);
      enableCancelShowroomButton();
      showErrorMessages(svc, errors);
    },
    getMetadata: addToShowroomSchema,
  });

  const projectLinks = lens.prop(PROJECT_LINKS).get();

  const isWithAtLeastOneRequiredFieldInvalid =
    lens.prop(INDUSTRIES).toProps().isInvalid ||
    lens.prop(DOCS_DECKS).toProps().isInvalid ||
    lens.prop(SLIDE_DECK).toProps().isInvalid;

  const showLeaveConfirm = useLeaveConfirm(modalProps, isChanged);
  const totalSteps = () => steps.length;

  const isLastStep = (n = 1) => activeStep === totalSteps() - n;

  const handleNext = () => {
    completeStep();
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrevious = () => {
    setActiveStep((currentAciveStep) => currentAciveStep - 1);
  };

  const completeStep = () => {
    const newCompleted = completed;
    newCompleted[activeStep + 1] = true;
    setCompleted(newCompleted);
  };

  const stepContent: { [key: number]: ReactJSXElement } = {
    1: <AboutProject lens={lens} className="add-to-showroom-modal" />,
    2: (
      <RequiredArtifactsForm
        lens={lens}
        dictionary={dictionary}
        projectLinks={projectLinks}
        isReachedLinkLimit={isReachedLinkLimit}
        className="add-to-showroom-modal"
        currentLinkCount={currentLinkCount}
        gitlabUrl={gitlabUrl}
        info={info}
      />
    ),
    3: (
      <OptionalArtifactsForm
        lens={lens}
        dictionary={dictionary}
        projectLinks={projectLinks}
        isReachedLinkLimit={isReachedLinkLimit}
        className="add-to-showroom-modal"
        currentLinkCount={currentLinkCount}
        info={info}
      />
    ),
  };

  useEffect(() => {
    (async () => {
      const linksDictionary: LinkDictionary =
        (await ProjectManagementService.getProjectLinks()) ?? { items: [] };
      setDictionary(linksDictionary.items);
    })();
  }, []);

  useEffect(() => {
    setCurrentLinkCount(projectLinks.length + 1); // add + 1 because 'Code in Gitlab' link is not part of the user's projectLinks but is the part of users info
  }, [projectLinks]);

  return (
    <ModalBlocker blockerShadow="dark" {...modalProps} abort={showLeaveConfirm}>
      <ModalWindow cx="add-to-showroom-modal">
        <ModalHeader
          borderBottom
          title="Add to Showroom"
          onClose={showLeaveConfirm}
          cx={'add-to-showroom-modal__header'}
        />
        <StepperController
          activeStep={activeStep}
          steps={steps}
          stepClick={setActiveStep}
          stepsCompleted={completed}
          enableAllSteps
        />
        {activeStep === 0 && isWithAtLeastOneRequiredFieldInvalid && (
          <WarningMessage
            message="Before adding a project to the Showroom, the below requirements have to be satisfied:"
            className={'add-to-showroom-modal__warning-alert'}
          />
        )}
        <ScrollBars
          hasTopShadow
          hasBottomShadow
          cx="add-to-showroom-modal__main"
        >
          {stepContent[activeStep + 1]}
        </ScrollBars>
        <Footer
          lens={lens}
          saveForm={save}
          validate={validate}
          isNotLastStep={!isLastStep(1)}
          isFirstStep={activeStep === 0}
          setNextStep={handleNext}
          setPreviousStep={handlePrevious}
          setActiveStep={setActiveStep}
          projectLinks={projectLinks}
          projectId={parseInt(info.id)}
        />
      </ModalWindow>
    </ModalBlocker>
  );
}
export default AddToShowroom;
